import React from 'react';
import { useTranslation } from 'react-i18next';

function summarize(filter, type, t) {
  if (!filter) return '';
  if (type === 'text') {
    // New model: {condition, likeMode, not, value, order}
    if (filter.condition !== undefined) {
      const { not, condition, likeMode, value, order } = filter;
      let label;
      if (condition === 'IS NULL') {
        label = not ? t('table.notEmpty') : t('table.isEmpty');
      } else if (condition === 'LIKE') {
        const mode =
          likeMode === 'begins' ? t('table.beginsWith') :
          likeMode === 'ends'   ? t('table.endsWith')   :
          t('table.containsMode');
        label = not ? `NOT ${mode} "${value}"` : `${mode} "${value}"`;
      } else {
        label = not ? `NOT ${condition} "${value}"` : `${condition} "${value}"`;
      }
      if (order) label += order === 'asc' ? ' ↑' : ' ↓';
      return label;
    }
    // Legacy model: {op, value}
    const opKey = {
      contains: 'containsMode', equals: 'equals', starts_with: 'startsWith',
      ends_with: 'endsWith', is_empty: 'isEmpty', not_empty: 'notEmpty',
    };
    if (filter.op === 'is_empty' || filter.op === 'not_empty') return t(`table.${opKey[filter.op]}`);
    return `${t(`table.${opKey[filter.op] || filter.op}`)} "${filter.value}"`;
  }
  if (type === 'date') {
    const quick = { today: 'today', last_7d: 'last7d', last_30d: 'last30d' };
    if (quick[filter.op]) return t(`table.${quick[filter.op]}`);
    if (filter.op === 'before') return `${t('table.before')} ${filter.value}`;
    if (filter.op === 'after')  return `${t('table.after')} ${filter.value}`;
    if (filter.op === 'custom') return `${filter.value || '?'} – ${filter.value2 || '?'}`;
    return filter.op;
  }
  if (type === 'number') {
    const opKey = { eq: 'equals', gt: 'greaterThan', lt: 'lessThan', between: 'between', is_empty: 'isEmpty', not_empty: 'notEmpty' };
    if (filter.op === 'is_empty' || filter.op === 'not_empty') return t(`table.${opKey[filter.op]}`);
    if (filter.op === 'between') return `${filter.value} – ${filter.value2}`;
    return `${t(`table.${opKey[filter.op] || filter.op}`)} ${filter.value}`;
  }
  if (type === 'select') {
    return (filter.values || []).join(', ');
  }
  return '';
}

export default function ActiveFilterBar({ columns, columnFilters, onClear, onClearAll }) {
  const { t } = useTranslation();
  const active = (columns || []).filter(c => columnFilters[c.key]);
  if (active.length === 0) return null;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      {active.map(col => (
        <span key={col.key} style={S.chip}>
          <span style={{ color: 'var(--text-muted)', marginRight: 3 }}>{col.label}:</span>
          {summarize(columnFilters[col.key], col.type, t)}
          <button
            onClick={() => onClear(col.key)}
            style={S.x}
            aria-label={`Remove ${col.label} filter`}
          >
            ×
          </button>
        </span>
      ))}
      <button onClick={onClearAll} style={S.clearAll}>
        {t('table.clearAllFilters')}
      </button>
    </div>
  );
}

const S = {
  chip: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '2px 6px 2px 8px',
    background: 'var(--accent-dim)', border: '1px solid var(--accent)',
    borderRadius: 4, fontSize: 11, color: 'var(--accent)',
    fontFamily: 'var(--font-mono)',
  },
  x: {
    background: 'none', border: 'none',
    color: 'var(--accent)', cursor: 'pointer',
    fontSize: 14, lineHeight: 1, padding: '0 1px',
    fontFamily: 'inherit',
  },
  clearAll: {
    background: 'none', border: '1px solid var(--border)',
    borderRadius: 4, padding: '2px 8px',
    fontSize: 11, color: 'var(--text-muted)',
    cursor: 'pointer', fontFamily: 'inherit',
  },
};
